import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, IdCard, Building2, FileSignature, Wallet, Globe, Users, Plus, Star } from "lucide-react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { ContactForm, contactName } from "./PartnerContacts";
import { NotesCard } from "../components/RecordTasksNotes";
import { RecordDocuments } from "../components/RecordDocuments";
import api from "../lib/api";
import { formatCurrency, formatDate, formatPhone } from "../lib/utils-format";
import { FIN_KIND_LABEL } from "../config/homebuilding";

const dirName = (r) => [r.first_name, r.last_name].filter(Boolean).join(" ") || r.company_name || "Directory Entry";

function Info({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent || "text-primary"}`} />
      </div>
      <div className="mt-2 font-display text-xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

export default function DirectoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showContact, setShowContact] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadContacts = () => api.get(`/partner_contacts?company_id=${id}&limit=200`).then((r) => setContacts(r.data.items || []));

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/directory/${id}`),
      api.get(`/financial_entries?vendor_id=${id}&limit=500`),
      api.get(`/partner_contacts?company_id=${id}&limit=200`),
    ]).then(async ([e, fe, ct]) => {
      setEntry(e.data);
      setEntries(fe.data.items || []);
      setContacts(ct.data.items || []);
      const ids = e.data.project_ids || [];
      if (ids.length) {
        const all = await api.get("/projects?limit=500");
        const map = {};
        (all.data.items || []).forEach((p) => { map[p.id] = p; });
        setProjects(ids.map((pid) => map[pid]).filter(Boolean).map((p) => ({
          id: p.id, label: p.project_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Project",
          phase: p.lead_status || p.status,
        })));
      }
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading || !entry)
    return (
      <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
        <Skeleton className="h-8 w-64 mb-6" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      </div>
    );

  const commitments = entries.filter((e) => ["contract", "purchase_order"].includes(e.kind));
  const other = entries.filter((e) => !["contract", "purchase_order"].includes(e.kind));
  const totalCommitted = commitments.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const totalPaid = entries.filter((e) => e.kind === "payment").reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const commitCols = [
    { key: "kind", label: "Type", render: (e) => FIN_KIND_LABEL[e.kind] || e.kind },
    { key: "description", label: "Description", render: (e) => <span className="font-medium">{e.description}</span> },
    { key: "project_name", label: "Project", render: (e) => e.project_name || "—" },
    { key: "category", label: "Category", render: (e) => e.category || "—" },
    { key: "amount", label: "Amount", render: (e) => formatCurrency(e.amount) },
    { key: "status", label: "Status", badge: true },
    { key: "entry_date", label: "Date", render: (e) => formatDate(e.entry_date) },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <button onClick={() => navigate("/directory")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="dirdetail-back">
        <ArrowLeft className="h-4 w-4" />Partners
      </button>
      <PageHeader
        title={entry.company_name || dirName(entry)}
        subtitle={[entry.type, entry.trade].filter(Boolean).join(" · ") || "Company"}
        actions={<StatusBadge value={entry.status} />}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="rounded-md border border-border bg-card p-5 space-y-4 lg:col-span-1" data-testid="dirdetail-contact">
          <div className="text-sm font-semibold mb-1">Contact</div>
          {entry.company_name && <Info icon={Building2} label="Company" value={entry.company_name} />}
          {entry.website && (
            <Info icon={Globe} label="Website" value={
              <a href={/^https?:\/\//i.test(entry.website) ? entry.website : `https://${entry.website}`} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all" data-testid="dirdetail-website">
                {entry.website.replace(/^https?:\/\//i, "").replace(/\/$/, "")}
              </a>
            } />
          )}
          <Info icon={Mail} label="Email" value={entry.email} />
          <Info icon={Phone} label="Phone" value={formatPhone(entry.mobile_phone || entry.business_phone)} />
          <Info icon={IdCard} label="License #" value={entry.license_no} />
          <Info icon={MapPin} label="Location" value={[entry.city, entry.state].filter(Boolean).join(", ")} />
          {entry.notes && <div className="pt-2 border-t border-border text-sm text-muted-foreground">{entry.notes}</div>}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Stat icon={Building2} label="Projects" value={projects.length} />
            <Stat icon={FileSignature} label="Committed" value={formatCurrency(totalCommitted)} accent="text-violet-500" />
            <Stat icon={Wallet} label="Paid" value={formatCurrency(totalPaid)} accent="text-emerald-500" />
          </div>
          <div className="rounded-md border border-border bg-card p-5">
            <div className="text-sm font-semibold mb-3">Projects on</div>
            {projects.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not linked to any projects yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {projects.map((p) => (
                  <span key={p.id}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-sm"
                    data-testid={`dirdetail-project-${p.id}`}>
                    <Building2 className="h-3.5 w-3.5 text-primary" />{p.label}
                    {p.phase && <span className="text-xs text-muted-foreground">· {p.phase}</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-md border border-border bg-card p-5 mb-6" data-testid="company-contacts-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold"><Users className="h-4 w-4 text-primary" />Contacts <span className="text-muted-foreground font-normal">({contacts.length})</span></div>
          <Button size="sm" variant="outline" onClick={() => { setEditContact(null); setShowContact(true); }} data-testid="company-add-contact-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add Contact</Button>
        </div>
        {contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No contacts yet. Add the people you work with at this company.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {contacts.map((c) => (
              <button key={c.id} onClick={() => { setEditContact(c); setShowContact(true); }} className="text-left rounded-md border border-border p-3 hover:bg-accent/50 transition-colors" data-testid={`company-contact-${c.id}`}>
                <div className="flex items-center gap-1.5 font-medium">{contactName(c)}{c.is_primary && <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />}</div>
                {c.title && <div className="text-xs text-muted-foreground">{c.title}</div>}
                {c.email && <div className="text-xs text-muted-foreground mt-1">{c.email}</div>}
                {(c.mobile_phone || c.business_phone) && <div className="text-xs text-muted-foreground">{formatPhone(c.mobile_phone || c.business_phone)}</div>}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <NotesCard recordId={id} resource="directory" />
        <RecordDocuments recordId={id} />
      </div>

      <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><FileSignature className="h-4 w-4 text-primary" />Contracts & Purchase Orders</div>
      <DataTable columns={commitCols} items={commitments} loading={false} emptyLabel="No contracts or POs recorded for this vendor" />

      {other.length > 0 && (
        <>
          <div className="flex items-center gap-2 mt-6 mb-3 text-sm font-semibold"><Wallet className="h-4 w-4 text-primary" />Other Financial Activity</div>
          <DataTable columns={commitCols} items={other} loading={false} emptyLabel="No other activity" />
        </>
      )}

      <ContactForm open={showContact} onOpenChange={setShowContact} initial={editContact} companies={entry ? [entry] : []} lockedCompanyId={id} onSaved={loadContacts} />
    </div>
  );
}
